import React, { useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import useOfferUI from '../../../api/useOffersUI';
import ActionCell from '../../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../../components/DataTableComponent/DataTable';
import DialogModal from '../../../components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from '../../../components/DialogModalManager/constants/modalConfigs';
import EmptyState from '../../../components/EmptyState/EmptyState';
import FilterSearchCard from '../../../components/Filter/FilterSearchCard';
import PortalHeader from '../../../components/PortalHeader/PortalHeader';
import { DATE_TIME_FORMAT } from '../../../constants/constants';
import { permissionsUtil } from '../../../utils/permissionsUtil';
import { EToasterStatus, showToast } from '../../../utils/showToast';

import './style.scss';

const dayjsUTC = dayjs.extend(utc);

const OffersUITable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { getOffersUI, deleteOfferUI } = useOfferUI(publisherId);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOfferUI, setSelectedOfferUI] = useState<string | null>(null);
  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const handleDeleteOfferUI = async () => {
    if (!selectedOfferUI) return;
    await deleteOfferUI.mutate(selectedOfferUI, {
      onSuccess: () => {
        showToast({
          message: 'Offer Design deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffersUI.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete Offer Design',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setShowDeleteModal(false);
        setSelectedOfferUI(null);
      }
    });
  };

  const handleEditOfferUI = (offerId: string): void => {
    navigate(`./form/${offerId}`);
  };

  const handleDuplicateOffer = (offerId: string): void => {
    navigate(`./dup/${offerId}`);
  };

  const handleDeletePopup = (offerId: string) => {
    if (!offerId) return;
    setSelectedOfferUI(offerId);
    setShowDeleteModal(true);
  };

  const COLUMNS: ColumnDef<any>[] = [
    {
      accessorKey: 'image',
      header: 'Image',
      cell: ({ row }: any) => {
        return row.original.backgroundImage ? (
          <img
            style={{
              objectFit: 'cover',
              width: '32px',
              height: '32px'
            }}
            src={row.original.backgroundImage}
            alt="emblem"
          />
        ) : (
          <Typography>N/A</Typography>
        );
      }
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => getValue(),
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.name || '';
        const nameB = rowB.original.name || '';
        return nameA.localeCompare(nameB);
      }
    },
    {
      accessorKey: 'externalId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: '_id',
      header: 'ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ getValue }: any) => {
        if (getOffersUI.isLoading) return '';
        return <span>{dayjsUTC.utc(getValue()).format(DATE_TIME_FORMAT)}</span>;
      },
      sortDescFirst: true,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.createdAt;
        const dateB = rowB.original.createdAt;

        // Handle null/undefined values - put them at the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dayjsUTC.utc(dateB).diff(dayjsUTC.utc(dateA));
      }
    },
    {
      accessorKey: 'offerUiType',
      header: 'Type',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: any) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => handleEditOfferUI(row.original._id),
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.OFFER_DESIGN,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            },
            {
              label: 'Duplicate',
              handler: () => handleDuplicateOffer(row.original._id),
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.OFFER_DESIGN,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            },
            {
              label: 'Delete',
              handler: () => handleDeletePopup(row.original._id),
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.OFFER_DESIGN,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            }
          ]}
        />
      )
    }
  ];

  const offers = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data) {
      return [];
    }
    return getOffersUI.data.result;
  }, [getOffersUI.data, getOffersUI.isLoading]);

  const filteredOffers = useMemo(() => {
    return offers.filter((offer: any) => {
      const lowerExternalId = offer.externalId?.toLowerCase() || '';
      const lowerName = offer.name?.toLowerCase() || '';
      return (
        lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
        lowerName.includes(nameFilter.toLowerCase())
      );
    });
  }, [offers, externalIdFilter, nameFilter]);

  const BUTTON_TEXT = `${offers.length > 0 ? 'Add' : 'Create'} New Design`;

  const renderContent = () => {
    if (getOffersUI.isLoading || getOffersUI.isFetching) {
      return (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      );
    }

    if (offers.length === 0) {
      return (
        <EmptyState
          title="Make your offers pop"
          description="Create your first offer design by hitting the 'Add new design' button on the top right corner of the screen"
          buttonText={BUTTON_TEXT}
          disableButton={
            !permissionsUtil.isActionEnabled(
              null,
              EPermissionGroup.OFFER_DESIGN,
              EPermissionAction.MANAGE,
              publisherId
            )
          }
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredOffers}
          columns={COLUMNS}
          rowIdIdentifier="_id"
          bodyDataTestId="dashOffersDesignList"
          rowsPerPage={offers.length}
          showPagination={false}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title="Offer Design"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !permissionsUtil.canUserEdit(),
          hidden: !permissionsUtil.isActionEnabled(
            null,
            EPermissionGroup.OFFER_DESIGN,
            EPermissionAction.MANAGE,
            publisherId
          )
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Offer Design External ID"
            filterValue={externalIdFilter}
            setFilterValue={setExternalIdFilter}
            isLabelSearchBy
          />
          <FilterSearchCard
            header="Offer Design Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
          />
        </div>
      )}
      {renderContent()}
      {showDeleteModal && (
        <DialogModal
          isOpen={showDeleteModal}
          onOpenChange={(open) => {
            if (!open) setShowDeleteModal(false);
          }}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_UI_POPUP].title}
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_UI_POPUP].description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOfferUI },
            onClose: {
              label: 'Cancel',
              action: () => setShowDeleteModal(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default OffersUITable;
